import React, { useState } from 'react';
import Col from 'react-bootstrap/Col';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Row from 'react-bootstrap/Row';
import Tooltip from 'react-bootstrap/Tooltip';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import PlanCardDiscountSwitch from '../../components/PlanCardDiscountSwitch';
import { LocalizedString } from '../../py-generated/datalouder/shared/types';

const FREE_NOT_AVAILABLE_FEATURES: LocalizedString[] = [
  {
    en: 'Daily Data Analysis Report',
    zh: '系統每日分析',
  },
  {
    en: '"Trading Squad" WhatsApp Channel, discover 10x return trading opportunity',
    zh: '"高手圈" WhatsApp Channel，發掘交易機會',
  },
  {
    en: 'Effective "Leek Strategy" as a trading signal',
    zh: '獨創韭菜策略',
  },
];

const FREE_AVAILABLE_FEATURES: LocalizedString[] = [
  {
    en: "Participants' trading data and changes",
    zh: '大行持股數據及變化',
  },
  {
    en: 'Stock price & information',
    zh: '股票報價 及 信息',
  },
  {
    en: '<b>Partial</b> Access to Ranking Table (Powerful Stock Screener)',
    zh: '<b>部分</b>排行榜功能（選股神器）',
  },
];

const VIP_FEATURES: LocalizedString[] = [
  {
    en: "Participants' trading data and changes",
    zh: '大行持股數據及變化',
  },
  {
    en: 'Stock price & information',
    zh: '股票報價 及 信息',
  },
  {
    en: '<b>Full</b> Access to Ranking Table (Powerful Stock Screener)',
    zh: '<b>全部</b>排行榜功能（選股神器）',
  },
];

interface Props {
  month?: 1 | 6 | 12;
  freeTrialDisabled?: boolean;
  hideSwitch?: boolean;
}

const ORIGINAL_PRICE_PER_MOUNTH = {
  1: '799.99',
  6: '583.99',
  12: '524.99',
};
const FLEX_START = 'flex-start';

const renderFeatureItem = (feature: string, available: boolean) => (
  <div
    className="feature"
    key={feature}
    style={{
      display: 'flex',
      alignItems: FLEX_START,
      justifyContent: FLEX_START,
      marginBottom: 15,
    }}
  >
    <div
      style={{
        display: 'flex',
        marginTop: 4,
        marginRight: 10,
        borderRadius: '50%',
        width: '1.2rem',
        minWidth: '1.2rem',
        height: '1.2rem',
        fontSize: '0.8rem',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: available ? '#EBFFF3' : '#F8EAEB',
        color: available ? '#008533' : '#BD2B33',
        fontWeight: 'bolder',
      }}
    >
      {available ? <>&#10003;</> : <>&#10007;</>}
    </div>
    <div
      style={{
        textAlign: 'start',
      }}
      dangerouslySetInnerHTML={{ __html: feature }}
    />
  </div>
);

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  word-wrap: break-word;
  background-color: #fff;
  background-clip: border-box;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.12rem;
  padding: 40px;
  margin-top: 1rem;
  box-shadow: 0 0.125rem 0.25rem rgb(0 0 0 / 8%);
`;

const PlanPrice = styled.div`
  display: flex;
  justify-content: center;
  font-size: 42px;
  line-height: 1;
  margin-right: 5px;
`;

const PlanTitle = styled.div`
  margin-top: 30px;
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 15px;
`;

const PlanSubtitle = styled.div`
  font-size: 14px;
  font-weight: normal;
  color: #888888;
  line-height: 1.5;
  text-align: start;
`;

const PlanInterval = styled(PlanSubtitle)`
  padding-bottom: 5px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 35px;
`;

const OriginalPrice = styled.div`
  text-decoration-line: line-through;
`;

const FreeTrialText = styled.div<Props>`
  margin-top: 1rem;
  color: #800000;
  font-size: 1.6rem;
  border: solid 2px #800000;
  padding: 0 3px;
  border-radius: 5px;
  margin-bottom: -20px;
  text-decoration-line: ${p => (p.freeTrialDisabled ? 'line-through' : '')};
`;

const PlanCards: React.FC<Props> = ({
  month,
  freeTrialDisabled,
  hideSwitch,
}) => {
  const { t, i18n } = useTranslation();
  const [privateMonth, setMonth] = useState<1 | 6 | 12>(month || 6);
  const selectedMonth = month || privateMonth;

  return (
    <Row>
      <Col xs={12} md={6}>
        <Card>
          <div
            style={{ display: 'flex', width: '100%', alignItems: 'flex-end' }}
          >
            <PlanPrice>$0</PlanPrice>
          </div>
          <PlanTitle>{t('plan-basic-title')}</PlanTitle>
          <PlanSubtitle>{t('plan-basic-subtitle')}</PlanSubtitle>

          <FeatureList>
            {FREE_NOT_AVAILABLE_FEATURES.map(feature =>
              renderFeatureItem(feature[i18n.language], false)
            )}
            {FREE_AVAILABLE_FEATURES.map(feature =>
              renderFeatureItem(feature[i18n.language], true)
            )}
          </FeatureList>
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card>
          {(selectedMonth === 12 || selectedMonth === 6) && (
            <OriginalPrice>$799.99 /{t('/ Month')}</OriginalPrice>
          )}
          <div
            style={{
              display: 'flex',
              width: '100%',
              alignItems: 'flex-end',
              justifyContent: FLEX_START,
            }}
          >
            <PlanPrice>${ORIGINAL_PRICE_PER_MOUNTH[selectedMonth]}</PlanPrice>
            <PlanInterval>/{t(`/ Month`)}</PlanInterval>
          </div>
          <PlanTitle>{t('plan-pro-title')}</PlanTitle>
          <PlanSubtitle>{t('plan-pro-subtitle')}</PlanSubtitle>

          <OverlayTrigger
            trigger={['click', 'hover']}
            placement="top"
            overlay={
              <Tooltip id="only-one-time-free-trial'">
                {t('each-user-only-one-time-free-trial')}
              </Tooltip>
            }
          >
            <FreeTrialText freeTrialDisabled={freeTrialDisabled}>
              {t('plan-pro-free-trial')}
            </FreeTrialText>
          </OverlayTrigger>

          <FeatureList>
            {FREE_NOT_AVAILABLE_FEATURES.map(feature =>
              renderFeatureItem(feature[i18n.language], true)
            )}
            {VIP_FEATURES.map(feature =>
              renderFeatureItem(feature[i18n.language], true)
            )}
          </FeatureList>
          {!hideSwitch && (
            <PlanCardDiscountSwitch
              selectedMonth={selectedMonth}
              handleSwitch={setMonth}
              isMonthRestricted={() => false}
            />
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default PlanCards;
